import React from "react";
import { SimulationSummaryResponse } from "../types";
import NavChart from "./NavChart";
import { intervalToDuration, formatDuration } from "date-fns";

type Props = { data: SimulationSummaryResponse | null };

export default function Report({ data }: Props) {
  if (!data) return null;

  const { signals, yearly, milestones, notables, currency } = data;
  const target = milestones?.targetReached ?? null;
  const cur = symbolFor(currency);

  // Simulaation aloitus keston laskentaan
  const simStartIso =
    yearly?.[0]?.periodStart ??
    null;
  const simStart = simStartIso ? new Date(simStartIso) : null;

  // Taulukoiden varma nouseva lajittelu
  const yearlySorted = (yearly ?? []).slice().sort((a, b) => a.periodStart.localeCompare(b.periodStart));

  // SMA200-diff prosentteina
  const smaDiff = signals && signals.sma200
    ? (signals.lastClose - signals.sma200) / signals.sma200
    : null;

  // Outcome-tyylit
  const outcomeCardClasses = "bg-light border border-2 text-body";

  const outcomeBadgeClasses = target
    ? "badge bg-success-subtle text-success-emphasis"
    : "badge bg-danger-subtle text-danger-emphasis";

  const outcomeIcon = target ? "✓" : "✕";   // vihreä check tai punainen X
  const outcomeIconColor = target ? "text-success" : "text-danger";

  const ta = data.targetAnalysis;
  const requiredOwnContributionPerMonthAmount = ta?.requiredOwnContributionPerMonthAmount ?? null;
  const requiredMonthlySavingsPlanAmount = ta?.requiredMonthlySavingsPlanAmount ?? null;
  const reqLump = ta?.requiredLumpSumAmount ?? null;
  const hasRequiredOwnContributionPerMonthAmount = requiredOwnContributionPerMonthAmount != null && requiredOwnContributionPerMonthAmount > 0;
  const hasRequiredMonthlySavingsPlanAmount = requiredMonthlySavingsPlanAmount != null && requiredMonthlySavingsPlanAmount > 0;
  const hasLump = reqLump != null && reqLump > 0;
  const hasReturn = ta?.requiredAnnualReturn != null;

  return (
    <div className="card shadow-sm p-4">
      <h2 className="mb-3">Report</h2>

      {/* Outcome – korostettu, hillitty callout */}
      <section className={`mb-4 p-3 rounded-3 ${outcomeCardClasses}`} role="status" aria-live="polite">
        <div className="d-flex align-items-start gap-3">
          <div className={`fs-3 lh-1 ${outcomeIconColor}`} aria-hidden="true">
            {outcomeIcon}
          </div>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h3 className="h5 mb-0">Outcome</h3>
              <span className={outcomeBadgeClasses}>
                {target ? "Target reached" : "Not reached"}
              </span>
            </div>

            {target ? (
              <p className="mb-0 mt-1 fw-medium">
                Target {formatMoney(target.amount, cur)} reached on {formatDate(target.date)}
                {simStart && (
                  <> (
                    {formatDuration(
                      intervalToDuration({ start: simStart, end: new Date(target.date) }),
                      { format: ["years", "months", "days"], delimiter: ", " }
                    )}
                    )</>
                )}
              </p>
            ) : (
              <>
                <p className="mb-0 mt-1">
                  You won’t reach the target with your current plan. Reaching it would require one of the following:
                </p>

                {ta && (
                  <div className="mt-2">
                    <ul className="mb-0 ms-0 ps-3" style={{ listStylePosition: "outside" }}>
                      {hasRequiredOwnContributionPerMonthAmount && (
                        <li>
                          Increase own contribution per month to <span className="fw-semibold">{formatMoney(requiredOwnContributionPerMonthAmount!, cur)}/mo</span>
                        </li>
                      )}

                      {hasRequiredMonthlySavingsPlanAmount && (
                        <li>
                          Increase monthly savings plan amount to <span className="fw-semibold">{formatMoney(requiredMonthlySavingsPlanAmount!, cur)}/mo</span>
                        </li>
                      )}

                      {hasLump && (
                        <li>
                          Make a one-time lump-sum investment of <span className="fw-semibold">{formatMoney(reqLump!, cur)}</span>
                        </li>
                      )}

                      {hasReturn && (
                        <li>
                          Keep current contributions, but you’d need an average annual return of{" "}
                          <span className="fw-semibold">{formatPct(ta.requiredAnnualReturn)}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}

              </>
            )}
          </div>
        </div>
      </section>


      <NavChart data={data} />

      <section className="mb-3">
        <h5 className="text-secondary">Yearly summary</h5>
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead className="sticky-top bg-white">
              <tr>
                <th>Year</th>
                <th className="text-end">Market value</th>
                <th className="text-end">Account balance</th>
                <th className="text-end">NAV</th>
                <th className="text-end">Invested</th>
                <th className="text-end">Deposited</th>
                <th className="text-end">Interest</th>
                <th className="text-end">Leverage</th>
              </tr>
            </thead>
            <tbody>
              {yearlySorted.map((y) => (
                <tr key={y.periodStart}>
                  <td>{new Date(y.periodStart).getFullYear()}</td>
                  <td className="text-end text-nowrap">{formatMoney(y.endMarketValue, cur)}</td>
                  <td className="text-end text-nowrap">{formatMoney(y.endAccountBalance, cur)}</td>
                  <td className="text-end text-nowrap">{formatMoney(y.endNav, cur)}</td>
                  <td className="text-end text-nowrap">{formatMoney(y.amountInvested, cur)}</td>
                  <td className="text-end text-nowrap">{formatMoney(y.amountDeposited, cur)}</td>
                  <td className="text-end text-nowrap">{formatMoney(y.interestPaid, cur)}</td>
                  <td className="text-end text-nowrap">{formatPct0(y.endLeveragePercent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-3">
        <h5 className="text-secondary">Statistics</h5>
        <ul className="pt-2 mb-2">
          {notables?.maxLeverage && (
            <li>
              Max leverage: {formatPct0(notables.maxLeverage.value)} on {formatDate(notables.maxLeverage.date)}
            </li>
          )}
          {notables?.maxDebt && (
            <li>
              Max debt: {formatMoney(notables.maxDebt.value, cur)} on {formatDate(notables.maxDebt.date)}
            </li>
          )}
          {notables?.maxInterest && (
            <li>
              Largest interest charge: {formatMoney(notables.maxInterest.value, cur)} on{" "}
              {formatDate(notables.maxInterest.date)}
            </li>
          )}
          {notables?.maxBuy && (
            <li>
              Largest buy: {formatMoney(notables.maxBuy.value, cur)} on {formatDate(notables.maxBuy.date)}
            </li>
          )}
        </ul>
      </section>

      {/* Leverage-cap periods */}
      {notables?.leverageCapPercent != null &&
        <section className="mb-3">
          <h5 className="text-secondary">
            Leverage-cap periods (&gt; {formatPct0(notables.leverageCapPercent)})
          </h5>
          {notables.overCapPeriods && notables.overCapPeriods.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>End</th>
                    <th className="text-end">Length (days)</th>
                  </tr>
                </thead>
                <tbody>
                  {notables.overCapPeriods.map((p, idx) => (
                    <tr key={`${p.start}-${p.end}-${idx}`}>
                      <td>{formatDate(p.start)}</td>
                      <td>{formatDate(p.end)}</td>
                      <td className="text-end">{p.lengthDays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mb-2">No periods above the leverage cap.</p>
          )}
        </section>
      }

      <section className="mb-0">
        <h5 className="text-secondary">Market signals</h5>
        <ul className="pt-2 mb-2">
          <li>Index: {signals.indexSymbol}</li>
          <li>Drawdown from ATH: {formatPct(signals.drawdown)}</li>
          <li>
            SMA200 diff: {smaDiff != null ? formatPct(smaDiff) : "-"}{" "}
            (last {signals.lastClose.toFixed(2)} | SMA200 {signals.sma200.toFixed(2)})
          </li>
          <li>
            EURUSD deviation: {formatPct(signals.eurUsdDeviation)} (last {signals.eurUsdLast.toFixed(4)} | long mean{" "}
            {signals.eurUsdLongMean.toFixed(4)})
          </li>
        </ul>
      </section>
    </div>
  );
}

/* ---------- helpers ---------- */

function symbolFor(currency?: string): string {
  switch (currency) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    default:
      return "€";
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("fi-FI", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

function formatMoney(n: number | null | undefined, cur: string): string {
  if (n == null) return "-";
  const hasCents = Math.abs(n % 1) > 1e-6;
  return (
    new Intl.NumberFormat("fi-FI", {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(n) + " " + cur
  );
}

function formatPct(value: number | null | undefined): string {
  if (value == null) return "-";
  return (value * 100).toFixed(2) + " %";
}

function formatPct0(value: number | null | undefined): string {
  if (value == null) return "-";
  return (value * 100).toFixed(0) + " %";
}
